import { Switch } from "@/shared/foundation/primitives";

import { ParameterError } from "./ParameterError";
import { useParameterRecord } from "./parameterValues";

export interface SwitchParameterProps {
  name: string;
  parameterKey: string;
  label: string;
  description?: string;
  /** Whether the action treats the absent key as on. */
  defaultOn: boolean;
  /** How the action spells a boolean: `true` or `True`. */
  spelling: "lower" | "title";
}

function isOn(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) {
    return fallback;
  }
  return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
}

/**
 * A boolean parameter as a switch. Turning it back to the default removes the
 * key rather than writing the default out.
 */
export function SwitchParameter({
  name,
  parameterKey,
  label,
  description,
  defaultOn,
  spelling,
}: SwitchParameterProps) {
  const record = useParameterRecord(name);
  const checked = isOn(record.get(parameterKey), defaultOn);
  const spell = (on: boolean) => {
    const word = on ? "true" : "false";
    return spelling === "title" ? word[0].toUpperCase() + word.slice(1) : word;
  };

  return (
    <div data-testid={`param-${parameterKey}`}>
      <Switch
        checked={checked}
        onCheckedChange={(next) => {
          record.write({
            [parameterKey]: next === defaultOn ? undefined : spell(next),
          });
        }}
        label={label}
        description={description}
      />
      <ParameterError message={record.error(parameterKey)} />
    </div>
  );
}
